import api from './api';

const timeTrackingService = {
  // Get all time entries with filters
  getAllTimeEntries: async (filters = {}) => {
    const params = new URLSearchParams();
    
    if (filters.user_id) params.append('user_id', filters.user_id);
    if (filters.project_id) params.append('project_id', filters.project_id);
    if (filters.task_id) params.append('task_id', filters.task_id);
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    if (filters.status) params.append('status', filters.status);
    if (filters.is_billable !== undefined) params.append('is_billable', filters.is_billable);
    if (filters.page) params.append('page', filters.page);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.sort_by) params.append('sort_by', filters.sort_by);
    if (filters.sort_order) params.append('sort_order', filters.sort_order);

    const response = await api.get(`/time-entries?${params.toString()}`);
    return response.data;
  },

  // Get single time entry
  getTimeEntry: async (id) => {
    const response = await api.get(`/time-entries/${id}`);
    return response.data;
  },

  // Create time entry
  createTimeEntry: async (data) => {
    const response = await api.post('/time-entries', data);
    return response.data;
  },

  // Update time entry
  updateTimeEntry: async (id, data) => {
    const response = await api.put(`/time-entries/${id}`, data);
    return response.data;
  },

  // Delete time entry
  deleteTimeEntry: async (id) => {
    const response = await api.delete(`/time-entries/${id}`);
    return response.data;
  },

  // Start timer
  startTimer: async (data) => {
    const response = await api.post('/time-entries/timer/start', data);
    return response.data;
  },

  // Stop timer
  stopTimer: async (id) => {
    const response = await api.put(`/time-entries/timer/${id}/stop`);
    return response.data;
  },

  // Get running timer
  getRunningTimer: async () => {
    const response = await api.get('/time-entries/timer/running');
    return response.data;
  },

  // Get statistics
  getStatistics: async (filters = {}) => {
    const params = new URLSearchParams();
    
    if (filters.user_id) params.append('user_id', filters.user_id);
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    if (filters.group_by) params.append('group_by', filters.group_by);

    const response = await api.get(`/time-entries/statistics?${params.toString()}`);
    return response.data;
  },

  // Approve time entry
  approveTimeEntry: async (id) => {
    const response = await api.put(`/time-entries/${id}/approve`);
    return response.data;
  },

  // Export time entries (client-side CSV generation)
  exportToCSV: (entries) => {
    const headers = [
      'Date',
      'Project',
      'Task',
      'Description',
      'Hours',
      'Billable',
      'Rate',
      'Amount',
      'Status'
    ];

    const rows = entries.map(entry => [
      entry.entry_date,
      entry.project?.name || '',
      entry.task?.title || '',
      entry.description || '',
      entry.hours_spent,
      entry.is_billable ? 'Yes' : 'No',
      entry.hourly_rate || '',
      entry.is_billable && entry.hourly_rate 
        ? (parseFloat(entry.hours_spent) * parseFloat(entry.hourly_rate)).toFixed(2)
        : '',
      entry.status
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `time-entries-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  },

  // Calculate duration from start and end time
  calculateDuration: (startTime, endTime) => {
    if (!startTime || !endTime) return 0;
    const start = new Date(startTime);
    const end = new Date(endTime);
    const hours = (end - start) / (1000 * 60 * 60);
    return Math.max(0, parseFloat(hours.toFixed(2)));
  },

  // Format duration for display
  formatDuration: (hours) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  },

  // Get date range presets
  getDateRangePresets: () => {
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const startOfYear = new Date(today.getFullYear(), 0, 1);

    return {
      today: {
        start_date: today.toISOString().split('T')[0],
        end_date: today.toISOString().split('T')[0]
      },
      thisWeek: {
        start_date: startOfWeek.toISOString().split('T')[0],
        end_date: today.toISOString().split('T')[0]
      },
      thisMonth: {
        start_date: startOfMonth.toISOString().split('T')[0],
        end_date: today.toISOString().split('T')[0]
      },
      thisYear: {
        start_date: startOfYear.toISOString().split('T')[0],
        end_date: today.toISOString().split('T')[0]
      },
      last7Days: {
        start_date: new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        end_date: today.toISOString().split('T')[0]
      },
      last30Days: {
        start_date: new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        end_date: today.toISOString().split('T')[0]
      }
    };
  },

  getUnbilledSubmissions: async ({ page = 1, limit = 20,project_id=0 } = {}) => {
  const response = await api.get('/time-entries/task/submissions?', {
    params: { page, limit, project_id }
  });
  return response.data; 
},



 submissionTimeEntry: async (data) => {
    const response =  await api.post('/time-entries/createSubmission',data);;
    return response.data;
  },


};

export default timeTrackingService;

